// src/utils/useCurrentUser.ts
//
// Sdílený hook na přihlášeného uživatele a jeho dokument users/{uid}.
// Jeden onAuthStateChanged + getDoc místo kopií v obrazovkách; přesměrování
// neřeší – nepřihlášené odchytává RequireAuth v App.tsx a chování obrazovek
// se nemění (user je null, dokud se nepřihlásí).
//
// Přečtený dokument (i zjištění, že neexistuje) předá AuthProvideru – účet
// v hlavičce tak má čerstvé jméno a číslo bez dalšího čtení (utils/authProfile).

import { useEffect, useState } from "react";
import { onAuthStateChanged, User } from "firebase/auth";
import { DocumentData, doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { useProfileSync } from "./authProfile";
import { reportError, reportWarning } from "./reportError";

export interface CurrentUserState {
  /** Přihlášený uživatel Firebase Auth, nebo null (odhlášen / ještě se načítá). */
  user: User | null;
  /** Data dokumentu users/{uid}; null, dokud se nenačte, neexistuje nebo čtení selže. */
  userDoc: DocumentData | null;
  /** True, dokud Firebase nevyhodnotí persistenci přihlášení a nedoběhne getDoc. */
  loading: boolean;
  /** True, když getDoc proběhl a dokument users/{uid} neexistuje (ne chyba čtení). */
  missing: boolean;
}

/**
 * Vrací { user, userDoc, loading } z onAuthStateChanged + getDoc(users/{uid}).
 * Pomalé getDoc nepřepíše novější auth stav (sekvenční čítač jako v useAuth);
 * po odmountování se už žádný setState nestane.
 */
export function useCurrentUser(): CurrentUserState {
  const syncProfile = useProfileSync();
  const [state, setState] = useState<CurrentUserState>({
    user: null,
    userDoc: null,
    loading: true,
    missing: false,
  });

  useEffect(() => {
    let seq = 0;
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const my = ++seq;
      if (!user) {
        setState({ user: null, userDoc: null, loading: false, missing: false });
        return;
      }
      let userDoc: DocumentData | null = null;
      let missing = false;
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (snap.exists()) {
          userDoc = snap.data();
        } else {
          missing = true;
          reportWarning("useCurrentUser", "Uživatelský dokument neexistuje.", {
            uid: user.uid,
          });
        }
      } catch (err) {
        reportError("useCurrentUser", err, { uid: user.uid });
      }
      if (my !== seq) return;
      setState({ user, userDoc, loading: false, missing });
      // Chyba čtení (userDoc null, ne missing) hlavičce nic nepředává.
      if (userDoc || missing) syncProfile(user.uid, userDoc);
    });
    return () => {
      // Zneplatní rozběhnuté getDoc (odmountování) – bez setState po unmountu.
      seq++;
      unsubscribe();
    };
  }, [syncProfile]);

  return state;
}

export default useCurrentUser;
